/**
 * DanaVoice - API Client
 *
 * HTTP client for making requests to the backend API.
 * Handles authentication, error handling, and request/response formatting.
 */

import { stackClientApp } from '../stack';
import type {
  APIResponse,
  PaginationParams,
  SortParams,
  SearchParams,
} from '../../../shared/types/api';

/**
 * API Configuration
 */
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8787';
const API_VERSION = 'v1';

/**
 * HTTP Methods
 */
type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

/**
 * Request Options
 */
interface RequestOptions {
  method?: HttpMethod;
  body?: unknown;
  headers?: Record<string, string>;
  params?: Record<string, string | number | boolean | undefined>;
  requiresAuth?: boolean;
}

/**
 * API Error
 */
export class APIError extends Error {
  constructor(
    public statusCode: number,
    public code: string,
    message: string,
    public details?: unknown
  ) {
    super(message);
    this.name = 'APIError';
  }
}

/**
 * Timeouts. Every await that crosses the network or the auth SDK must be bounded — an
 * unbounded one leaves the UI on a spinner with nothing logged and no way to recover.
 */
const REQUEST_TIMEOUT_MS = 30000;
const AUTH_TOKEN_TIMEOUT_MS = 10000;

/**
 * Reject if a promise has not settled within `ms`.
 */
function withTimeout<T>(promise: Promise<T>, ms: number, message: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(message)), ms);
    promise.then(
      (value) => { clearTimeout(timer); resolve(value); },
      (error) => { clearTimeout(timer); reject(error); }
    );
  });
}

/**
 * Token Storage (Legacy - keeping for backward compatibility)
 */
class TokenStorage {
  private static ACCESS_TOKEN_KEY = 'dana_access_token';
  private static REFRESH_TOKEN_KEY = 'dana_refresh_token';

  /**
   * Get access token from Stack Auth or fallback to localStorage
   */
  static async getAccessToken(): Promise<string | null> {
    try {
      // Get token from Stack Auth session
      const user = await stackClientApp.getUser();
      if (user) {
        // Stack Auth handles tokens internally via cookies
        // We need to get the access token for API requests
        const tokenInfo = await user.getAuthJson();
        return tokenInfo?.accessToken || null;
      }
    } catch (error) {
      console.error('Failed to get Stack Auth token:', error);
    }
    
    // Fallback to localStorage for backward compatibility
    return localStorage.getItem(this.ACCESS_TOKEN_KEY);
  }

  static setAccessToken(token: string): void {
    localStorage.setItem(this.ACCESS_TOKEN_KEY, token);
  }

  static getRefreshToken(): string | null {
    return localStorage.getItem(this.REFRESH_TOKEN_KEY);
  }

  static setRefreshToken(token: string): void {
    localStorage.setItem(this.REFRESH_TOKEN_KEY, token);
  }

  static clearTokens(): void {
    localStorage.removeItem(this.ACCESS_TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
  }
}

/**
 * API Client
 */
class APIClient {
  private baseURL: string;

  constructor() {
    this.baseURL = `${API_BASE_URL}/${API_VERSION}`;
  }

  /**
   * Make HTTP request
   */
  private async request<T>(
    endpoint: string,
    options: RequestOptions = {}
  ): Promise<T> {
    const {
      method = 'GET',
      body,
      headers = {},
      params = {},
      requiresAuth = true,
    } = options;

    // Build URL with query parameters
    const url = new URL(`${this.baseURL}${endpoint}`);
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        url.searchParams.append(key, String(value));
      }
    });

    // Build headers
    const requestHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
      ...headers,
    };

    // Add authentication header if required.
    // Bounded: getAccessToken() calls into the Neon Auth SDK, which can stall on a stale
    // refresh cookie. Without a bound this hangs the caller forever — which left the dashboard
    // stuck on its loading spinner with no error to surface.
    if (requiresAuth) {
      const token = await withTimeout(
        TokenStorage.getAccessToken(),
        AUTH_TOKEN_TIMEOUT_MS,
        'Timed out retrieving auth token'
      ).catch((err) => {
        console.error('[API] Auth token retrieval failed:', err);
        return null;
      });

      if (token) {
        requestHeaders['Authorization'] = `Bearer ${token}`;
      }
    }

    // Make request, bounded by a timeout so a stalled connection surfaces as an error
    // instead of an indefinite pending promise.
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

    let response: Response;
    try {
      response = await fetch(url.toString(), {
        method,
        headers: requestHeaders,
        body: body ? JSON.stringify(body) : null,
        credentials: 'include', // Important: Include cookies for Stack Auth
        signal: controller.signal,
      });
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        throw new APIError(
          408,
          'REQUEST_TIMEOUT',
          `Request to ${endpoint} timed out after ${REQUEST_TIMEOUT_MS / 1000}s`
        );
      }
      throw err;
    } finally {
      clearTimeout(timeoutId);
    }

    // Parse response
    const data: APIResponse<T> = await response.json();

    // Handle errors
    if (!response.ok || !data.success) {
      const error = data.error || {
        code: 'UNKNOWN_ERROR',
        message: 'An unknown error occurred',
        statusCode: response.status,
      };

      throw new APIError(
        error.statusCode,
        error.code,
        error.message,
        error.details
      );
    }

    return data as unknown as T;
  }

  /**
   * GET request
   */
  async get<T>(
    endpoint: string,
    params?: Record<string, string | number | boolean | undefined>,
    requiresAuth = true
  ): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'GET',
      params,
      requiresAuth,
    });
  }

  /**
   * POST request
   */
  async post<T>(
    endpoint: string,
    body?: unknown,
    requiresAuth = true
  ): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body,
      requiresAuth,
    });
  }

  /**
   * PUT request
   */
  async put<T>(
    endpoint: string,
    body?: unknown,
    requiresAuth = true
  ): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body,
      requiresAuth,
    });
  }

  /**
   * PATCH request
   */
  async patch<T>(
    endpoint: string,
    body?: unknown,
    requiresAuth = true
  ): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body,
      requiresAuth,
    });
  }

  /**
   * DELETE request
   */
  async delete<T>(
    endpoint: string,
    requiresAuth = true
  ): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'DELETE',
      requiresAuth,
    });
  }

  /**
   * Build query string from pagination, sort, and search params
   */
  buildListParams(
    pagination?: PaginationParams,
    sort?: SortParams,
    search?: SearchParams,
    filters?: Record<string, string | number | boolean | undefined>
  ): Record<string, string | number | boolean | undefined> {
    return {
      ...(pagination?.page && { page: pagination.page }),
      ...(pagination?.limit && { limit: pagination.limit }),
      ...(sort?.sortBy && { sortBy: sort.sortBy }),
      ...(sort?.sortOrder && { sortOrder: sort.sortOrder }),
      ...(search?.q && { q: search.q }),
      ...filters,
    };
  }
}

/**
 * Export singleton instance
 */
export const apiClient = new APIClient();

/**
 * Export token storage for use by auth service
 */
export { TokenStorage };